import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Box, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Swal from 'sweetalert2';

import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb, SCUISnackbar } from '../../atomic/molecules';
import { SCUIButton } from '../../atomic/atoms';
import { GetYdMpOrderQuery } from '../../services/query';
import { enumSnackbarStatusType } from '../../types/enums';

export const MpLabelPrint: FC = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [originCodeList, setOriginCodeList] = useState<{ id: string; value: string }[]>([]);
    const [selectedOriginCode, setSelectedOriginCode] = useState<string>('');
    const [trackingId, setTrackingId] = useState<string>('');
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [snackbarShow, setSnackbarShow] = useState(false);
    const [snackbarStatus, setSnackbarStatus] = useState<enumSnackbarStatusType>(enumSnackbarStatusType.success);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    useEffect(() => {
        getMpOrigins(false);
    }, []);

    const getMpOrigins = async (props: any) => {
        GetYdMpOrderQuery.GetMpOrigins(props)
            .then((res: any) => {
                if (res) {
                    let orgTemp: { id: string; value: string }[] = [];
                    res.forEach((x: any) => {
                        let obj = { id: x.originCode, value: x.name };
                        orgTemp.push(obj);
                    });
                    setOriginCodeList(orgTemp);
                }
            })
            .catch(() => {
                Swal.fire(t('general.error'), t('mpLabelPrint.originCodeFetchError'), 'error');
            });
    };

    const fetchPackageDetail = async () => {
        const trimmedTrackingId = trackingId.trim();

        if (!selectedOriginCode) {
            Swal.fire(t('general.error'), t('mpLabelPrint.originCodeRequired'), 'warning');
            return;
        }
        if (!trimmedTrackingId) {
            Swal.fire(t('general.error'), t('mpLabelPrint.trackingIdRequired'), 'warning');
            return;
        }

        setLoading(true);
        setPdfUrl(null);

        try {
            const res = await GetYdMpOrderQuery.GetPackageDetail({
                OriginCode: selectedOriginCode,
                TrackingID: trimmedTrackingId,
            });

            if (res && res.data) {
                if (res.data.base64) {
                    const pdfDataUri = `data:application/pdf;base64,${res.data.base64}`;
                    setPdfUrl(pdfDataUri);

                    setSnackbarStatus(enumSnackbarStatusType.success);
                    setSnackbarMessage(t('mpLabelPrint.downloadSuccess'));
                    setSnackbarShow(true);
                } else if (res.data.url) {
                    const link = document.createElement('a');
                    link.href = res.data.url;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    setSnackbarStatus(enumSnackbarStatusType.success);
                    setSnackbarMessage(t('mpLabelPrint.downloadSuccess'));
                    setSnackbarShow(true);
                } else {
                    const errorMessage = res.data.message || t('mpLabelPrint.defaultErrorMessage');
                    Swal.fire(t('general.error'), errorMessage, 'error');
                }
            } else {
                Swal.fire(t('general.error'), t('mpLabelPrint.defaultErrorMessage'), 'error');
            }
        } catch {
            Swal.fire(t('general.error'), t('mpLabelPrint.defaultErrorMessage'), 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <SCUIContent>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <SCUIBreadCrumb title={t('mpLabelPrint.title')} componentLink="mpLabelPrint" />
            </Grid>

            <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid size={{ xs: 12, md: 3 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="mp-label-print-origin-label">
                                    {t('mpLabelPrint.marketplace')}
                                </InputLabel>
                                <Select
                                    labelId="mp-label-print-origin-label"
                                    value={selectedOriginCode}
                                    label={t('mpLabelPrint.marketplace')}
                                    onChange={(e) => setSelectedOriginCode(e.target.value)}
                                >
                                    {originCodeList.map((origin) => (
                                        <MenuItem key={origin.id} value={origin.id}>
                                            {origin.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('mpLabelPrint.trackingId')}
                                value={trackingId}
                                onChange={(e) => setTrackingId(e.target.value)}
                                placeholder={t('mpLabelPrint.trackingIdPlaceholder')}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        fetchPackageDetail();
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 'auto' }}>
                            <SCUIButton
                                type="primary"
                                sizeType="default"
                                size="m"
                                text={loading ? t('mpLabelPrint.loading') : t('mpLabelPrint.search')}
                                showStartIcon
                                startIcon={<SearchIcon />}
                                onClick={fetchPackageDetail}
                                disabled={loading}
                                buttonType="button"
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {pdfUrl && (
                <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {t('mpLabelPrint.labelPreview', 'Etiket Önizleme')}
                            </Typography>
                            <SCUIButton
                                type="primary"
                                sizeType="default"
                                size="m"
                                text={t('mpLabelPrint.downloadLabel', 'Etiketi İndir / Görüntüle')}
                                onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = pdfUrl;
                                    link.download = `Label_${trackingId}.pdf`;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                }}
                                buttonType="button"
                            />
                        </Box>
                        <Box sx={{ width: '100%', height: '800px', border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                            <iframe
                                src={pdfUrl}
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                                title={t('mpLabelPrint.labelPreview', 'Etiket Önizleme')}
                            />
                        </Box>
                    </CardContent>
                </Card>
            )}

            <SCUISnackbar
                show={snackbarShow}
                messageText={snackbarMessage}
                status={snackbarStatus}
                autoHideDuration={5000}
                onClose={() => {
                    setSnackbarShow(false);
                    setSnackbarMessage('');
                }}
            />
        </SCUIContent>
    );
};
